/* eslint-disable no-useless-escape */
import axios from "axios";
import { endOfMonth, format, startOfMonth } from "date-fns";

import { env } from "../../../utils/env";
import IGuidance from "../types/IGuidance";
import { formatDateTime } from "../../../utils/neo4jHelper";
import IIndicationsRequest from "../../addFOBIndicationPanel/types/IIndicationsRequest";

export default class GuidanceService {
  private static readonly url = env.webApiBaseUrl;

  private static async get(
    endpoint: string,
    queryString?: string,
    body?: string,
  ) {
    try {
      let url = `${this.url}${endpoint}`;
      if (queryString) {
        url += `?${queryString}`;
      }

      const result = await axios({
        url,
        method: "GET",
        data: body,
      });

      return result;
    } catch (e) {
      const err = e as any;
      console.error("API Error", err?.response);

      if (err?.response?.status === 0 || err?.response?.status === 401) {
        throw new Error("Permissions denied");
      }

      if (err.response.data?.errorMessage) {
        throw new Error(err.response.data.errorMessage);
      }

      throw e;
    }
  }

  public static async proxy(
    cypherName: string,
    params: { [key: string]: any },
  ): Promise<any[]> {
    const paramString = Object.keys(params)
      .map((key) => {
        if (params[key] === null || params[key] === undefined) {
          return undefined;
        }

        if (Array.isArray(params[key])) {
          if (params[key].length === 0) {
            return undefined;
          }

          return `${key}=${JSON.stringify(params[key])}`;
        }

        return `${key}=${params[key]}`;
      })
      .filter((r) => !!r)
      .join("&")
      .replace(/\|/g, "%7C")
      .replace(/\#/g, "%23");
    const response: any = await this.get(
      `/proxy/${cypherName}`,
      paramString,
      JSON.stringify({ params: JSON.stringify(params) }),
    );

    return response?.data || [];
  }

  public static async post(
    cypherName: string,
    body: { [key: string]: any },
  ): Promise<any> {
    try {
      const updatedBody = { ...body };

      Object.keys(updatedBody).forEach((key) => {
        if (updatedBody[key] === undefined || updatedBody[key] === null) {
          delete updatedBody[key];
        }
      });

      const result = await axios({
        url: `${this.url}/proxy/${cypherName}`,
        method: "POST",
        data: updatedBody,
      });

      return result;
    } catch (e) {
      const err = e as any;
      console.error("API Error", err?.response);

      if (err?.response?.status === 0 || err?.response?.status === 401) {
        throw new Error("Permissions denied");
      }

      if (err.response.data?.errorMessage) {
        throw new Error(err.response.data.errorMessage);
      }

      throw e;
    }
  }

  public static async getIndicationsByMonth(
    location: string,
    product: string,
    startDate: string,
    endDate: string,
  ): Promise<IGuidance[]> {
    const result = await this.proxy("IndicationsByMonth", {
      location,
      product,
      startDate,
      endDate,
    });

    return result.map((r) => {
      const month = String(r.month).length === 1 ? `0${r.month}` : r.month;
      return {
        date: format(new Date(`${r.year}-${month}-01T00:00:00`), "yyyy-MM-dd"),
        fobPrice: r.maxPrice,
        altPrice: r.minPrice,
      };
    });
  }

  public static async getIndicationsWithinMonth(
    location: string,
    product: string,
    month: string,
  ): Promise<IGuidance[]> {
    const result = await this.proxy("IndicationsWithinMonth", {
      location,
      product,
      month,
    });

    return result.map((r) => {
      return {
        date: formatDateTime(r.loggedDateTime),
        fobPrice: r.maxPrice,
        altPrice: r.minPrice,
      };
    });
  }

  public static async createGuidance(params: {
    records: IGuidance[];
    userName: string;
    currency: string;
    uom: string;
    product: string;
    location: string;
  }) {
    const competitor =
      params.product === "METHANOL"
        ? "Koch Methanol, LLC"
        : "Koch Fertilizer LLC";

    const request: IIndicationsRequest = {
      isKoch: true,
      contactName: params.userName,
      marketer: params.userName,
      currency: params.currency,
      uom: params.uom,
      party: "Koch Fertilizer LLC",
      indications: params.records.map((r) => {
        return {
          quantity: 0,
          postToTeams: false,
          product: params.product,
          prices: [
            {
              origin: params.location,
              fobMinPrice: r.altPrice,
              fobMaxPrice: r.fobPrice,
            },
          ],
          type: "FOB",
          priceType: "range",
          modes: ["TRUCK", "RAIL"],
          competitor,
          startDate: format(
            startOfMonth(new Date(`${r.date}T00:00`)),
            "yyyy-MM-dd",
          ),
          endDate: format(
            endOfMonth(new Date(`${r.date}T00:00`)),
            "yyyy-MM-dd",
          ),
          comments: "",
        };
      }),
    };

    console.info("createGuidance", request);
    const response: any = await this.post(`IndicationCreate`, request);

    return response?.data || [];
  }
}
